import { Waves, DollarSign, Calendar, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import type { UiDestination } from "@/hooks/useDestinations";

interface DestinationGridCardProps {
  destination: UiDestination;
  onClick: () => void;
  className?: string;
}

// Categorical, not a quality judgement -- routed through the --chart tokens
// instead of raw Tailwind colours so "Expert" doesn't read as a stoplight
// warning. --chart-3 is deliberately skipped: it's identical to --primary
// and would read as a call-to-action colour, not a category.
const SKILL_BADGE_CLASS: Record<string, string> = {
  beginner: "bg-chart-1",
  intermediate: "bg-chart-4",
  advanced: "bg-chart-5",
  expert: "bg-chart-2",
};

const capitalize = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const getCostDisplay = (rating: number): string => "$".repeat(Math.max(1, rating));

/**
 * Default destination-browsing card (grid on /destinations). Deliberately a
 * separate component from SurfSpotCard rather than a redesign of it --
 * SurfSpotCard is still used, unmodified, for the homepage globe's floating
 * overlay card, which has different size constraints and shouldn't inherit
 * changes made for a repeated-55-times grid context.
 */
export function DestinationGridCard({ destination, onClick, className }: DestinationGridCardProps) {
  const { image, name, country, skillLevel, skillLabel, waveType, crowdLevel, costRating, bestMonths } = destination;
  const displayLabel = skillLabel ?? capitalize(skillLevel);

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "group block w-full overflow-hidden rounded-xl border border-border bg-card text-left transition-colors hover:border-primary/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        className,
      )}
    >
      {/* Mobile is three-up and photo-first: aspect-square (vs the
          desktop/tablet aspect-[4/3]) gives the overlaid name enough height
          to wrap onto two lines without the image getting too squat. The
          stats grid and "View Details" below are hidden entirely on mobile
          (sm:grid / sm:block) -- at ~110px wide there's no room for them,
          and that detail is one tap away on the destination page. */}
      <div className="relative aspect-square sm:aspect-[4/3] w-full overflow-hidden">
        <img src={image} alt={name} className="h-full w-full object-cover" loading="lazy" />
        {/* Functional legibility scrim for the name/country overlay below --
            not decorative. Token-based (foreground, not a literal black) so
            it tracks the theme instead of being hardcoded. */}
        <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-t from-foreground/70 to-transparent" />
        {/* Shows the full displayLabel (skillLabel when the destination has
            one, e.g. "Beginner and Intermediate") on mobile too -- not the
            single-word skillLevel. A skill *range* collapsed to one word
            ("Intermediate") silently drops real information the destination
            data actually encodes. Mobile wraps the badge onto two lines
            (truncate is sm+-only) with a less pill-like rounding that reads
            better as a two-line block than a heavily rounded capsule would.
            Visual weight is reduced through tight padding and a narrow
            max-width (deliberately not widened just because the two-up grid
            gives each card more room than three-up did) rather than by
            shrinking the font past a legible floor -- 8px matches what was
            already verified readable in the three-up layout. */}
        <span
          className={cn(
            "absolute right-1 top-1 sm:right-2.5 sm:top-2.5 max-w-[48%] sm:max-w-[65%] rounded sm:rounded-full sm:truncate px-1 py-px sm:px-2.5 sm:py-1 text-[8px] sm:text-[11px] font-semibold leading-[1.2] text-background",
            SKILL_BADGE_CLASS[skillLevel] || "bg-primary",
          )}
        >
          {displayLabel}
        </span>
        <div className="absolute inset-x-0 bottom-0 p-1.5 sm:p-3">
          {/* No truncate on mobile: long names ("Mentawai Islands", "Puerto
              Escondido") are two natural words, so wrapping to two lines
              keeps the full name identifiable -- truncating to one line at
              this width would cut real names down to an unreadable
              fragment. sm:truncate restores the single-line desktop/tablet
              behaviour, where the card is wide enough that it isn't needed
              in practice. */}
          <h3 className="font-serif text-[10px] sm:text-lg font-semibold leading-[1.15] sm:leading-tight text-background sm:truncate">{name}</h3>
          <p className="text-[8px] sm:text-xs text-background/80 truncate">{country}</p>
        </div>
      </div>

      {/* One plain row of icon+text pairs, no bordered/boxed cells -- the
          four facts that matter for comparing destinations at a glance.
          min-w-0 + truncate on each cell: even at two-up tablet widths,
          values like "Moderate" or "Nov, Apr" could otherwise force the
          grid cell wider than its track, overflowing the card. */}
      <div className="hidden sm:grid grid-cols-2 gap-x-3 gap-y-2 p-3.5 text-xs text-muted-foreground">
        <span className="flex min-w-0 items-center gap-1.5">
          <Waves className="h-3.5 w-3.5 shrink-0 text-primary/70" aria-hidden />
          <span className="truncate">{capitalize(waveType)}</span>
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <DollarSign className="h-3.5 w-3.5 shrink-0 text-primary/70" aria-hidden />
          <span className="truncate">{getCostDisplay(costRating)}</span>
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <Calendar className="h-3.5 w-3.5 shrink-0 text-primary/70" aria-hidden />
          <span className="truncate">{bestMonths.slice(0, 2).join(', ') || '—'}</span>
        </span>
        <span className="flex min-w-0 items-center gap-1.5">
          <Users className="h-3.5 w-3.5 shrink-0 text-primary/70" aria-hidden />
          <span className="truncate">{capitalize(crowdLevel)}</span>
        </span>
      </div>

      <div className="hidden sm:block px-3.5 pb-3.5">
        <span className="text-xs font-semibold text-primary group-hover:underline">View Details &rarr;</span>
      </div>
    </button>
  );
}
